/** Map legacy reference paths (`/google-ads-seo/...`) to app routes. */
export function resolveReviewPath(reviewTo: string): string {
  if (!reviewTo || reviewTo === '#') return '/approval-queue';

  if (reviewTo.startsWith('/google-ads-seo/')) {
    return reviewTo.replace('/google-ads-seo', '') || '/';
  }

  const normalized = reviewTo.startsWith('/') ? reviewTo : `/${reviewTo}`;

  const gbpPostMatch = normalized.match(/^\/gbp\/posts\/([^/]+)\/?$/);
  if (gbpPostMatch?.[1]) {
    return `/gbp?tab=posts&post=${encodeURIComponent(gbpPostMatch[1])}`;
  }

  const gbpReviewMatch = normalized.match(/^\/gbp\/reviews\/([^/]+)\/?$/);
  if (gbpReviewMatch?.[1]) {
    return `/gbp?tab=reviews&review=${encodeURIComponent(gbpReviewMatch[1])}`;
  }

  const gbpQaMatch = normalized.match(/^\/gbp\/qa\/([^/]+)\/?$/);
  if (gbpQaMatch?.[1]) {
    return `/gbp?tab=qa&qa=${encodeURIComponent(gbpQaMatch[1])}`;
  }

  return normalized;
}

/** Extract article id from approval `review_to` or direct article paths. */
export function articleIdFromReviewPath(reviewTo: string): string | null {
  const path = resolveReviewPath(reviewTo);
  const match = path.match(/^\/articles\/([^/]+)\/?$/);
  return match?.[1] ?? null;
}

/** Extract landing-page id from review path or legacy `?id=` query. */
export function landingPageIdFromReviewPath(reviewTo: string): string | null {
  const path = resolveReviewPath(reviewTo);
  const routeMatch = path.match(/^\/landing-pages\/([^/]+)\/?$/);
  if (routeMatch?.[1] && routeMatch[1] !== 'preview') return routeMatch[1];

  try {
    const url = new URL(path, 'https://growth.local');
    const queryId = url.searchParams.get('id');
    return queryId || null;
  } catch {
    return null;
  }
}

/** Extract GBP post backend id from approval `review_to`. */
export function gbpPostIdFromReviewPath(reviewTo: string): string | null {
  const raw = reviewTo.startsWith('/') ? reviewTo : `/${reviewTo}`;
  const match = raw.match(/^\/gbp\/posts\/([^/]+)\/?$/);
  if (match?.[1]) return match[1];

  try {
    const url = new URL(resolveReviewPath(reviewTo), 'https://growth.local');
    return url.searchParams.get('post');
  } catch {
    return null;
  }
}

/** Extract GBP review backend id from approval `review_to`. */
export function gbpReviewIdFromReviewPath(reviewTo: string): string | null {
  const raw = reviewTo.startsWith('/') ? reviewTo : `/${reviewTo}`;
  const match = raw.match(/^\/gbp\/reviews\/([^/]+)\/?$/);
  if (match?.[1]) return match[1];

  try {
    const url = new URL(resolveReviewPath(reviewTo), 'https://growth.local');
    return url.searchParams.get('review');
  } catch {
    return null;
  }
}
